/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */
import i18next from 'i18next';
import _ from 'lodash';
import { attachmentTypes, isCombinedFile, MIME_TYPE_PDF, specialDocumentTypes } from './constants';

export const attachmentsFieldEventSuffix = 'attachments_field';
export const MAX_FILE_SIZE = 524288000;

export const typesWithoutPreview = [
  'text/xml',
  'text/html',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * Attachments mapper to FormData for the upload attachments endpoint.
 * @param files array with the uploader files
 * @param metaFieldNames array with the meta field names available for the current attachment type
 * @returns {FormData}
 */
export const mapToFormData = (files = {}, metaFieldNames = []) => {
  const data = new FormData();
  const metadataArray = [];

  files.forEach((file) => {
    const meta = {
      attachmentType: file.attachmentType,
      attachmentSubType: file.attachmentSubType,
      documentType: file.documentType,
    };

    metaFieldNames.forEach((metaFieldName) => {
      meta[metaFieldName] = file[metaFieldName];
    });

    if (isCombinedFile(file)) {
      meta.combinedFileTypeScopes = file.combinedFile.combinedFileTypeScopes;
    }

    metadataArray.push(meta);
    data.append('file', file);
  });

  data.append('meta', new Blob([JSON.stringify(metadataArray)], { type: 'application/json' }));

  return data;
};

export const getAttachmentLabel = (attachment) => {
  return i18next.t(`attachments.type.${attachment.attachmentType}`);
};

/**
 * Filters the attachments type configurations to keep only the specified category,
 * and to remove the unsupported attachment types.
 *
 * @param attachmentTypesConfig fo-configuration json for attachments
 * @param type root category of attachments (E.g. TECHNICAL)
 * @param excludedAttachmentTypes array with attachment types that have to be excluded from the given category
 * @returns {*}
 */
export const filterAttachmentConfigByType = (attachmentTypesConfig, type, excludedAttachmentTypes = []) => {
  return attachmentTypesConfig
    ?.filter(attachment => attachment.subType === type)
    ?.filter(attachment => excludedAttachmentTypes.includes && !excludedAttachmentTypes.includes(attachment.type))
    .sort((typeA, typeB) => {
      return typeA.order - typeB.order;
    });
};

export const getUnsupportedAttachmentTypes = (attachmentsUiSchema) => {
  return attachmentsUiSchema?.['ui:options']?.unsupportedAttachmentTypes || [];
};
export const getDisabledAttachmentTypes = (attachmentsUiSchema) => {
  return attachmentsUiSchema?.['ui:options']?.disabledAttachmentTypes || [];
};

/**
 * Identifies if the given attachment type has at least one uploaded file.
 *
 * @param attachmentsMetadata
 * @param attachmentType specific type (E.g. DRAWING)
 * @returns {boolean} true if that type is found as separate or combined attachment
 */
export const isAttachmentTypeUploaded = (attachmentsMetadata, attachmentType, dropdownDefinition) => {
  const foundInSimpleAttachments = attachmentsMetadata.filter(metadata => metadata.attachmentType === attachmentType);
  const foundInCombinedAttachments = attachmentsMetadata.filter(
    metadata =>
      metadata &&
      metadata.combinedFileTypeScopes &&
      metadata.combinedFileTypeScopes.find(scope => scope.type === attachmentType),
  );
  const allAttachmentsFound = foundInSimpleAttachments.concat(foundInCombinedAttachments);

  let allDropdownsValuesUploaded = true;
  if (dropdownDefinition) {
    const dropdownValuesFound = allAttachmentsFound.map(attachment => attachment[dropdownDefinition.name]);
    allDropdownsValuesUploaded = dropdownValuesFound.length === dropdownDefinition.enum.length;
  }
  return allAttachmentsFound.length > 0 && allDropdownsValuesUploaded;
};

export const getCombinedAttachmentTypes = (attachmentTypesConfig, excludedAttachmentTypes) => {
  const combinedFileConfig = _.first(
    attachmentTypesConfig?.filter(attachmentTypeConfig => attachmentTypeConfig?.type === specialDocumentTypes.COMBINED),
  );

  let { includedAttachmentTypes } = combinedFileConfig;
  if (includedAttachmentTypes) {
    includedAttachmentTypes = includedAttachmentTypes?.filter(
      attachmentType => excludedAttachmentTypes.includes && !excludedAttachmentTypes.includes(attachmentType),
    );
  } else {
    const technicalAttachmentTypes = filterAttachmentConfigByType(
      attachmentTypesConfig,
      attachmentTypes.TECHNICAL,
      excludedAttachmentTypes,
    );
    includedAttachmentTypes = technicalAttachmentTypes
      ?.filter(attachment => attachment.type !== specialDocumentTypes.COMBINED && !attachment.otherLanguage)
      .filter(attachment => attachment.acceptedMimeTypes?.indexOf(MIME_TYPE_PDF) !== -1)
      .map(type => type.type);
  }
  return includedAttachmentTypes;
};

/**
 * Identifies if there are at least 2 technical attachment subtypes left to be merged into a COMBINED attachment.
 */
export const isCombinedAttachmentPossible = (
  allAttachmentsMetadata,
  attachmentTypesConfig,
  excludedAttachmentTypes,
) => {
  let combinedAttachmentTypes = getCombinedAttachmentTypes(attachmentTypesConfig, excludedAttachmentTypes);

  allAttachmentsMetadata.forEach((attachmentMeta) => {
    // filter all uploaded file types from the available types list
    combinedAttachmentTypes = combinedAttachmentTypes?.filter(type => type !== attachmentMeta.attachmentType);
    if (attachmentMeta && attachmentMeta.combinedFileTypeScopes) {
      // filter all uploaded file types inside a combined file from the available types list
      for (const scope of attachmentMeta.combinedFileTypeScopes) {
        combinedAttachmentTypes = combinedAttachmentTypes?.filter(type => type !== scope.type);
      }
    }
  });

  return combinedAttachmentTypes?.length >= 2;
};

/**
 * Extracts the configuration for a single attachment type / subtype.
 * @param attachmentTypesConfig procedure config json
 * @param attachmentType root attachment type
 * @param attachmentSubType attachment subtype
 * @returns {*|null} single attachment subtype configuration
 */
export const getAttachmentConfig = (attachmentTypesConfig, attachmentType, attachmentSubType) => {
  if (!attachmentTypesConfig || !attachmentType || !attachmentSubType) {
    return null;
  }
  return attachmentTypesConfig.find(
    attachmentConfig => attachmentConfig.subType === attachmentType && attachmentConfig.type === attachmentSubType,
  );
};

// if there is a single subtype for the given attachment type, then that is the default subtype
// and could be already set on the subtype selector
export const getDefaultSubtype = (attachmentTypesConfig, attachmentType, attachmentsUiSchema) => {
  const excludedTypes = getUnsupportedAttachmentTypes(attachmentsUiSchema);
  const attachmentConfigs = filterAttachmentConfigByType(attachmentTypesConfig, attachmentType, excludedTypes);

  return attachmentConfigs?.length === 1 ? attachmentConfigs[0].type : '';
};

/**
 * Extracts the value of an attachment configuration attribute from the procedure config
 * or uses the default value from the ATTACHMENT_CONFIG_ATTRIBUTES definition.
 * @param attachmentConfig current attachment configuration
 * @param attachmentConfigAttribute one of the fields of ATTACHMENT_CONFIG_ATTRIBUTES
 * @returns {*}
 */
export const getAttachmentConfigValue = (attachmentConfig, attachmentConfigAttribute) => {
  return attachmentConfig?.[attachmentConfigAttribute.name] ?? attachmentConfigAttribute.default;
};

export const isAttachmentInputHidden = (attachmentTypesConfig, type) => {
  const config = _.first(attachmentTypesConfig?.filter(attachmentType => attachmentType?.type === type));
  return config?.hiddenInputs || [];
};

export const isCombinedTypeHidden = (attachmentTypesConfig, type) => {
  return _.first(
    _.filter(attachmentTypesConfig, { type: specialDocumentTypes.COMBINED }),
  )?.hiddenCombinedTypes?.includes(type);
};

/**
 * Filter RJSF attachment entries by attachment type.
 * @param attachmentsFormData array of attachments as they are modeled for the form
 * @param attachmentType root attachment type
 * @param userId optional user id provided if the attachments have to be filtered by user (e.g. signatures)
 * @returns {*[]}
 */
export const filterAttachmentsFormData = (attachmentsFormData, attachmentType, userId) => {
  let _attachmentsFormData = [];

  if (attachmentsFormData) {
    _attachmentsFormData = attachmentsFormData?.filter((attachment) => {
      return attachment.attachmentSubType === attachmentType;
    });

    if (userId) {
      _attachmentsFormData = _attachmentsFormData.filter((attachment) => {
        return attachment.uploaderId === userId;
      });
    }
  }
  return _attachmentsFormData;
};

export const mapDroppedFilesToFormData = (dropZoneFiles) => {
  return dropZoneFiles
    ? Object.values(dropZoneFiles).map(dropZoneFile => ({
      ...dropZoneFile,
      originalFileName: dropZoneFile.name,
      bytes: dropZoneFile.size,
    }))
    : [];
};
